import { StrategicAnalysis } from '../types';
import { AnalysisPages } from '../enums';
import { ExamLevels } from '../../../types/enum';

/**
 * What strategic analysis supports for an exam level. Exam levels without an entry support everything.
 */
export type ExamLevelSupport = {
	/** Only these pages are available (an allow list, so new pages stay hidden). Leave out to allow all pages */
	pages?: StrategicAnalysis.AnalysisPage[];
	/** These pages aren't available (a block list, for levels missing only a page or two) */
	excludedPages?: StrategicAnalysis.AnalysisPage[];
	/** Defaults to true */
	thermometer?: boolean;
	/** Defaults to true */
	pdfReport?: boolean;
};

/**
 * Exam level restrictions. Adding a restriction is a change here only - the page wrapper, saga guards and
 * PDF report read from this
 */
const examLevelSupport: Partial<Record<StrategicAnalysis.ExamLevels, ExamLevelSupport>> = {
	// No benchmarks (no MEPs, subject or banded benchmarks), so only the pages that don't need one
	[ExamLevels.TLevel]: {
		pages: [AnalysisPages.OVERVIEW, AnalysisPages.ATTAINMENT_PROFILE],
		thermometer: false,
		pdfReport: false,
	},
};

/**
 * Whether a page is available under the supplied exam level support
 * @param page The strategic analysis page
 * @param support The exam level's support, if it has any restrictions
 */
export const isPageAllowed = (
	page: StrategicAnalysis.AnalysisPage,
	support: ExamLevelSupport | undefined
): boolean => {
	if (!support) return true;
	if (support.pages && !support.pages.includes(page)) return false;
	return !support.excludedPages?.includes(page);
};

/**
 * Whether the strategic analysis page can be shown for the exam level
 * @param page The strategic analysis page
 * @param examLevel The exam level
 */
export const isPageSupportedForExamLevel = (
	page: StrategicAnalysis.AnalysisPage,
	examLevel: StrategicAnalysis.ExamLevels
): boolean => isPageAllowed(page, examLevelSupport[examLevel]);

/**
 * Whether the quality indicator thermometer is available for the exam level
 * @param examLevel The exam level
 */
export const isThermometerSupportedForExamLevel = (
	examLevel: StrategicAnalysis.ExamLevels
): boolean => examLevelSupport[examLevel]?.thermometer !== false;

/**
 * The exam levels that have no PDF report
 */
export const pdfReportExcludedExamLevels: StrategicAnalysis.ExamLevels[] = (Object.keys(
	examLevelSupport
).map(Number) as StrategicAnalysis.ExamLevels[]).filter(
	(examLevel) => examLevelSupport[examLevel]?.pdfReport === false
);

type ExamLevelSelectOption = { label: string; value: StrategicAnalysis.ExamLevels };
type ExamLevelSelectGroup = { label: string; options: ExamLevelSelectOption[] };

/**
 * Remove exam levels from the exam level select options, dropping any group that ends up empty
 * @param options The exam level options - groups and/or single options
 * @param excludeExamLevels The exam levels to remove
 */
export const excludeExamLevelOptions = (
	options: (ExamLevelSelectOption | ExamLevelSelectGroup)[],
	excludeExamLevels: number[]
): (ExamLevelSelectOption | ExamLevelSelectGroup)[] =>
	excludeExamLevels.length === 0
		? options
		: options
				.map((option) =>
					'options' in option
						? {
								...option,
								options: option.options.filter(
									(groupOption) => !excludeExamLevels.includes(groupOption.value)
								),
						  }
						: option
				)
				.filter((option) =>
					'options' in option
						? option.options.length > 0
						: !excludeExamLevels.includes(option.value)
				);
